import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/react'
import { useOutsideClick } from '../../../hooks'
import NavLink from '../NavLink'

const wrapperCss = css`
  position: relative;
`

const dropdownMenuCss = (theme) => css`
  flex-direction: column;
  align-content: flex-start;
  position: absolute;
  left: -50px;
  top: 50px;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.boxShadows.menu};
  border-radius: 4px;
  z-index: 100;
  padding: 10px 20px;
`

const dividerCss = (theme) => css`
  width: 100%;
  margin-top: 10px;
  margin-bottom: 20px;
  border-bottom: 2px solid ${theme.colors.gray100};
`

const DefaultSubItemsWrapper = ({ toggleDropdown, ...props }) => (
  <div className="page-item__dropdown" css={dropdownMenuCss} {...props} />
)

const PageItem = (props) => {
  const {
    type = 'link',
    showDropdownOnClick,
    href,
    label,
    subItems,
    customItem,
    onHover,
    subItemsWrapper: SubItemsWrapper = DefaultSubItemsWrapper,
    closeOnOutsideClick = true,
    onClick,
  } = props

  const wrapperRef = useRef(null)
  const [isDropdownOpen, setDropdownOpen] = useState(false)
  const toggleDropdown = () => setDropdownOpen(!isDropdownOpen)

  useOutsideClick(() => {
    if (
      subItems &&
      showDropdownOnClick &&
      isDropdownOpen &&
      closeOnOutsideClick
    ) {
      toggleDropdown()
    }
  }, wrapperRef)

  const handleMouseEnter = (e) => {
    if (onHover) {
      onHover(e, { href, label })
    }

    if (subItems && !showDropdownOnClick) {
      toggleDropdown()
    }
  }

  const handleMouseLeave = () => {
    if (subItems && !showDropdownOnClick) {
      toggleDropdown()
    }
  }

  const handleClick = (e) => {
    if (subItems && showDropdownOnClick) {
      toggleDropdown()
    }

    if (isDropdownOpen) {
      onClick?.(e)
    }
  }

  const getItem = (type) => {
    switch (type) {
      case 'link':
        return <NavLink isDropdownOpen={isDropdownOpen} {...props} />
      case 'divider':
        return <div className="page-item__divider" css={dividerCss} />
      case 'custom':
        return customItem && customItem({ ...props, isDropdownOpen })
      default:
        return null
    }
  }

  return (
    <div
      className="page-item"
      css={wrapperCss}
      ref={wrapperRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
      data-test="page-item"
    >
      {getItem(type)}

      {subItems && isDropdownOpen && (
        <SubItemsWrapper toggleDropdown={toggleDropdown}>
          {subItems.map((item) => (
            <PageItem key={item.label} className="page-item__link" {...item} />
          ))}
        </SubItemsWrapper>
      )}
    </div>
  )
}

DefaultSubItemsWrapper.propTypes = {
  toggleDropdown: PropTypes.func,
}

PageItem.propTypes = {
  type: PropTypes.string,
  href: PropTypes.string,
  alt: PropTypes.string,
  className: PropTypes.string,
  label: PropTypes.string.isRequired,
  subItems: PropTypes.array,
  showDropdownOnClick: PropTypes.bool,
  customItem: PropTypes.any,
  subItemsWrapper: PropTypes.any,
  onHover: PropTypes.func,
  onClick: PropTypes.func,
  closeOnOutsideClick: PropTypes.bool,
  partiallyActive: PropTypes.bool,
}

export default PageItem
